import { ThemeProvider } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import lightTheme from './themes/lightTheme';
import AppThemeProvider from './themes/AppThemeProvider';
import { AuthenticatedRoute } from './components/routes/AuthenticatedRoute';
import { AdminRoute } from './components/routes/AdminRoute';
import { useEffect, useRef, useState, lazy, Profiler, ProfilerOnRenderCallback } from 'react';
import { CreatorRoute } from './components/routes/CreatorRoute';
import { MarketingRoute } from './components/routes/MarketingRoute';
import LoadingWindow from './components/common/ui/LoadingWindow';
import PageSuspense from './components/common/ui/PageSuspense';
import AnalyticsTracker from './components/common/layout/AnalyticsTracker';
import { trackRender } from './components/debug/renderTracker';


// Lazy load all page components
// Main pages
const Dashboard = lazy(() => import('./pages/main/Dashboard'));
const Models = lazy(() => import('./pages/main').then(module => ({ default: module.Models })));
const Analytics = lazy(() => import('./pages/main').then(module => ({ default: module.Analytics })));
const HelpCenter = lazy(() => import('./pages/main').then(module => ({ default: module.HelpCenter })));
const ModelDetails = lazy(() => import('./pages/main/ModelDetails'));
const SmartPicks = lazy(() => import('./pages/main/SmartPicks'));
const Onboard = lazy(() => import('./pages/main/Onboard'));
const Tokens = lazy(() => import('./pages/main/Tokens'));
const Subscribe = lazy(() => import('./pages/main/Subscribe'));
const Moddybot = lazy(() => import('./pages/main/Moddybot'));

// Admin pages
const Users = lazy(() => import('./pages/admin').then(module => ({ default: module.Users })));
const Prototype1 = lazy(() => import('./pages/admin/Prototype1'));
const Labs = lazy(() => import('./pages/admin').then(module => ({ default: module.Labs })));
const ModelExplorer = lazy(() => import('./pages/admin').then(module => ({ default: module.ModelExplorer })));
const FeatureTemplates = lazy(() => import('./pages/admin/FeatureTemplates'));
const Ensembles = lazy(() => import('./pages/admin/Ensembles'));
const Outcomes = lazy(() => import('./pages/admin').then(module => ({ default: module.Outcomes })));
const OutcomeGroups = lazy(() => import('./pages/admin/OutcomeGroups'));
const EPickLists = lazy(() => import('./pages/admin/EPickLists'));
const BettingStrategies = lazy(() => import('./pages/admin/BettingStrategies'));
const StatsLibrary = lazy(() => import('./pages/admin/StatsLibrary'));

// Unauthenticated pages
const Login = lazy(() => import('./pages/unauthenticated/Login'));
const Signup = lazy(() => import('./pages/unauthenticated/Signup'));
const ResetPassword = lazy(() => import('./pages/unauthenticated/ResetPassword'));

// Public pages (accessible to both authenticated and unauthenticated users)

// Import Zustand stores and initializers
import {
  initializeAuth,
  initializeAppStore,
  useUserStore,
  useProfileStore,
  useAppStore,
  useModelStore,
  PREFERENCE_KEYS
} from './stores';
import { useApi } from './hooks/useApi';
import { useSocket } from './hooks/useSocket';
import TermsAndConditions from './pages/unauthenticated/TermsAndConditions';
import { debugService } from './services/debugService';
import UpdateNotification from './components/UpdateNotification';
import EjectionNotification from './components/common/EjectionNotification';
import MaintenancePage from './components/MaintenancePage';
import { useVersionCheck } from './hooks/useVersionCheck';
// Lazy load layout components
const LabSidebar = lazy(() => import('./components/common/layout/LabSidebar'));
const StandardLayout = lazy(() => import('./layouts/StandardLayout'));
const AdminSidebar = lazy(() => import('./components/common/layout/AdminSidebar'));

const App = (): JSX.Element => {
  const { maintenance } = useVersionCheck();
  const wasInMaintenance = useRef(false);
  const [recovering, setRecovering] = useState(false);

  // Track maintenance → normal transition so MaintenancePage stays mounted
  // long enough to show "We're back!" before reloading
  useEffect(() => {
    if (maintenance.enabled) {
      wasInMaintenance.current = true;
    } else if (wasInMaintenance.current) {
      wasInMaintenance.current = false;
      setRecovering(true);
      const timer = setTimeout(() => {
        window.location.reload();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [maintenance.enabled]);

  // Use separate selectors for each piece of state
  const setAppRefreshing = useAppStore(state => state.setAppRefreshing);
  const showMemoryMonitor = useAppStore(state => state.ui.showMemoryMonitor);
  const user = useUserStore(state => state.user);
  const authLoading = useUserStore(state => state.loading);
  const loadProfile = useProfileStore(state => state.loadProfile);
  const loadReferenceData = useAppStore(state => state.loadReferenceData);
  const api = useApi();
  const [initialized, setInitialized] = useState(false);

  // Initialize socket connection (hook handles auth state internally)
  // Disable during maintenance to avoid unnecessary WebSocket connections
  useSocket(maintenance.enabled);

  // Separate loading states for different parts of the app
  const isInitializing = !initialized;

  // Profiler callback for render tracking (only active when showMemoryMonitor is true)
  const onRenderCallback: ProfilerOnRenderCallback = (
    _id,
    _phase,
    _actualDuration,
    _baseDuration,
    _startTime,
    _commitTime
  ) => {
    if (showMemoryMonitor) {
      trackRender();
    }
  };

  // Initialize stores and load data
  useEffect(() => {
    let isInitialized = false;

    const initializeApp = async () => {
      if (isInitialized) return;
      isInitialized = true;

      try {
        // Don't set app refreshing here - let auth determine that

        // Initialize auth listener
        initializeAuth();
        
        // Initialize app store (window resize listener)
        const cleanup = initializeAppStore(lightTheme);
        
        setInitialized(true);
        return cleanup;
      } catch (error) {
        console.error('Failed to initialize app:', error);
        setInitialized(true); // Still set initialized to prevent infinite loading
      }
    };

    const cleanup = initializeApp();
    
    return () => {
      cleanup.then(fn => fn && fn());
    };
  }, []); // Empty dependency array since this should only run once

  // Load profile and reference data when user is authenticated
  useEffect(() => {
    if (maintenance.enabled) return;
    let isLoading = false;

    const loadAppData = async () => {
      if (isLoading) return;
      if (!authLoading && user) {
        try {
          isLoading = true;
          setAppRefreshing(true);

          // Load profile and reference data in parallel
          await Promise.all([
            loadProfile(api),
            loadReferenceData(api),
          ]);

          // Initialize debug service timing state from preference
          const timingPref = useProfileStore.getState().getPreference(PREFERENCE_KEYS.LAB_PARTNER_LOG_TIMING);
          debugService.setTimingEnabled(timingPref === 'true');

          // Initialize debug service ID logging state from preference
          const idsPref = useProfileStore.getState().getPreference(PREFERENCE_KEYS.LAB_PARTNER_LOG_IDS);
          debugService.setIdsEnabled(idsPref === 'true');

          // Only load models if a lab is selected
          const selectedLab = useProfileStore.getState().getSelectedLab();
          if (selectedLab) {
            // Load models - this sets labId properly for ModelDetails page
            useModelStore.getState().loadModelsForLab(api, selectedLab.id);
          }
        } catch (error) {
          console.error('Failed to load app data:', error);
        } finally {
          setAppRefreshing(false);
          isLoading = false;
        }
      } else if (!authLoading && !user) {
        // User is not authenticated
        setAppRefreshing(false);
      }
    };

    loadAppData();
  }, [user, authLoading, api, loadProfile, loadReferenceData, setAppRefreshing, maintenance.enabled]);

  // Background profile refresh when user returns to tab
  useEffect(() => {
    if (maintenance.enabled) return;
    let lastRefreshTime = 0;
    const DEBOUNCE_MS = 2000;

    const handleTabReturn = () => {
      // visibilitychange fires for visible, focus fires on window focus — deduplicate
      const now = Date.now();
      if (now - lastRefreshTime < DEBOUNCE_MS) return;
      if (!user) return;
      // For visibilitychange, only act when becoming visible
      if (document.visibilityState !== 'visible') return;

      lastRefreshTime = now;
      useProfileStore.getState().refreshProfile(api).catch(() => {});
    };

    document.addEventListener('visibilitychange', handleTabReturn);
    window.addEventListener('focus', handleTabReturn);

    return () => {
      document.removeEventListener('visibilitychange', handleTabReturn);
      window.removeEventListener('focus', handleTabReturn);
    };
  }, [user, api, maintenance.enabled]);

  // Show app immediately, but with different loading states
  if (isInitializing) {
    // Only show loading during critical initialization
    return (
      <ThemeProvider theme={lightTheme}>
        <CssBaseline />
        <LoadingWindow fullScreen={true} overlay={true} message="Initializing..." />
      </ThemeProvider>
    );
  }

  if (maintenance.enabled || recovering) {
    return (
      <MaintenancePage
        title={maintenance.title}
        message={maintenance.message}
        estimatedEnd={maintenance.estimatedEnd}
        recovered={recovering}
      />
    );
  }

  return (
    <AppThemeProvider>
      <Profiler id="app-root" onRender={onRenderCallback}>
        <BrowserRouter>
          <AnalyticsTracker />
          <Routes>
{/* Only allow login and signup for unauthenticated users */}
            <Route element={<MarketingRoute />}>
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
              {/* Redirect all other unhandled routes to login */}
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Route>

            {/* Root route with conditional rendering */}
            <Route element={<AuthenticatedRoute />}>
              {/* Subscribe page - accessible to authenticated users regardless of subscription status */}
              <Route path="subscribe" element={<PageSuspense><Subscribe /></PageSuspense>} />
              {/* Moddybot page - accessible to authenticated users, no sidebar */}
              <Route path="moddybot" element={<PageSuspense><Moddybot /></PageSuspense>} />

              {/* Protected workbench routes */}
              <Route element={<CreatorRoute />}>
                <Route element={<PageSuspense message="Loading Moddy AI..."><StandardLayout SidebarComponent={LabSidebar} /></PageSuspense>}>
                  <Route path="dashboard" element={<PageSuspense><Dashboard /></PageSuspense>} />
                  <Route path="models" element={<PageSuspense><Models /></PageSuspense>} />
                  <Route path="analytics" element={<PageSuspense><Analytics /></PageSuspense>} />
                  <Route path="smartpicks" element={<PageSuspense><SmartPicks /></PageSuspense>} />
                  <Route path="help" element={<PageSuspense><HelpCenter /></PageSuspense>} />
                  <Route path="model/:id" element={<PageSuspense><ModelDetails /></PageSuspense>} /> 
                  <Route path="onboard" element={<PageSuspense><Onboard /></PageSuspense>} />
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  {/* Redirect all other unhandled routes to dashboard */}
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Route>
              </Route>

              {/* Admin routes */}
              <Route element={<AdminRoute />}>
                <Route element={<PageSuspense><StandardLayout SidebarComponent={AdminSidebar} /></PageSuspense>}>
                  <Route path="/admin">
                    <Route index element={<PageSuspense><Users /></PageSuspense>} />
                    <Route path="labs" element={<PageSuspense><Labs /></PageSuspense>} />
                    <Route path="feature-templates" element={<PageSuspense><FeatureTemplates /></PageSuspense>} />
                    <Route path="ensembles" element={<PageSuspense><Ensembles /></PageSuspense>} />
                    <Route path="outcomes" element={<PageSuspense><Outcomes /></PageSuspense>} />
                    <Route path="outcome-groups" element={<PageSuspense><OutcomeGroups /></PageSuspense>} />
                    <Route path="epick-lists" element={<PageSuspense><EPickLists /></PageSuspense>} />
                    <Route path="betting-strategies" element={<PageSuspense><BettingStrategies /></PageSuspense>} />
                    <Route path="stats-library" element={<PageSuspense><StatsLibrary /></PageSuspense>} />
                    <Route path="tokens" element={<PageSuspense><Tokens /></PageSuspense>} />
                    <Route path="model-explorer" element={<PageSuspense><ModelExplorer /></PageSuspense>} />
                    <Route path="prototype1" element={<PageSuspense><Prototype1 /></PageSuspense>} />
                  </Route>
                </Route>
              </Route>
            </Route> {/* protected routes */}


          </Routes>
        </BrowserRouter>
        <UpdateNotification />
        <EjectionNotification />
      </Profiler>
    </AppThemeProvider>
  );
};


export default App; 